/**
 * usePermissions Hook
 * 
 * Central permissions logic for the application
 * Provides granular permission checks based on user role
 * 
 * Based on DEFAULT_PERMISSIONS_BY_ROLE from lib/constants/sharing.ts:
 * - owner: full access to everything
 * - admin: full access except deleting company
 * - editor: write access (create/edit) but cannot delete or access settings
 * - viewer: read-only access
 * 
 * @example
 * ```tsx
 * const { canCreate, canDelete, canAccess, isViewer } = usePermissions();
 * 
 * {canCreate('transactions') && (
 *   <Button>+ Novo Lançamento</Button>
 * )}
 * 
 * {canDelete('budgets') && (
 *   <Button variant="destructive">Deletar</Button>
 * )}
 * 
 * {canAccess('sharing') && (
 *   <MenuItem>Compartilhamento</MenuItem>
 * )}
 * ```
 */

import { useUserRole } from './useUserRole';
import { DEFAULT_PERMISSIONS_BY_ROLE } from '../lib/constants/sharing';
import type { MemberRole, ModulePermission } from '../types/sharing';

type Module = 'transactions' | 'budgets' | 'reports' | 'scenarios';
type RestrictedPage = 'settings' | 'sharing' | 'configurations';

interface PermissionsHook {
  // Current role state
  role: MemberRole | null;
  loading: boolean;
  
  // Role helpers
  isOwner: boolean;
  isAdmin: boolean;
  isEditor: boolean;
  isViewer: boolean;
  
  // Permission check functions
  canCreate: (module: Module) => boolean;
  canEdit: (module: Module) => boolean;
  canDelete: (module: Module) => boolean;
  canAccess: (page: RestrictedPage) => boolean;
  
  // Specific permissions
  canManageMembers: boolean;
  canDeleteCompany: boolean;
  canInviteUsers: boolean;
  canEditSettings: boolean;
  canExport: boolean;
  canSaveFilters: boolean;
}

/**
 * Check if a permission level allows a specific action
 */
function checkPermission(permission: ModulePermission | undefined, requiredLevel: 'read' | 'write' | 'full'): boolean {
  if (!permission || permission === 'none') return false;
  
  const levels: Record<ModulePermission, number> = {
    none: 0,
    read: 1,
    write: 2,
    full: 3,
  };
  
  const required = levels[requiredLevel];
  const current = levels[permission];
  
  return current >= required;
}

/**
 * Hook to check user permissions based on their role
 */
export function usePermissions(): PermissionsHook {
  const { role, loading, isOwner, isAdmin, isEditor, isViewer } = useUserRole();
  
  const permissions = role ? DEFAULT_PERMISSIONS_BY_ROLE[role] : {};

  /**
   * Check if user can create items in a module
   * Requires 'write' or 'full' permission
   */
  const canCreate = (module: Module): boolean => {
    return checkPermission(permissions[module], 'write');
  };

  /**
   * Check if user can edit items in a module
   * Requires 'write' or 'full' permission
   */
  const canEdit = (module: Module): boolean => {
    return checkPermission(permissions[module], 'write');
  };

  /**
   * Check if user can delete items in a module
   * Requires 'full' permission
   */
  const canDelete = (module: Module): boolean => {
    return checkPermission(permissions[module], 'full');
  };

  /**
   * Check if user can access a restricted page
   * Settings: owner/admin only
   * Sharing: owner/admin only
   * Configurations: owner/admin only
   */
  const canAccess = (page: RestrictedPage): boolean => {
    switch (page) {
      case 'settings':
        return checkPermission(permissions.settings, 'write');
      case 'sharing':
        // Only owner and admin can manage members
        return isOwner || isAdmin;
      case 'configurations':
        // Only owner and admin can access configurations
        return isOwner || isAdmin;
      default:
        return false;
    }
  };

  return {
    // Role state
    role,
    loading,
    
    // Role helpers
    isOwner,
    isAdmin,
    isEditor,
    isViewer,
    
    // Permission functions
    canCreate,
    canEdit,
    canDelete,
    canAccess,
    
    // Specific permissions
    canManageMembers: isOwner || isAdmin,
    canDeleteCompany: isOwner, // Only owner can delete company
    canInviteUsers: isOwner || isAdmin,
    canEditSettings: isOwner || isAdmin,
    canExport: true, // Everyone can export (including viewer)
    canSaveFilters: !isViewer, // Everyone except viewer can save filters
  };
}

